import { decryptPortfolioAssets } from "@/features/cryptography";
import { ResponseSuccess, streamResults } from "@/features/pe_aldd";
import { PortfolioAsset, supabaseApi } from "@/store/services/supabase";
import { dispatchWithRetries } from "@/store/services/supabase/utils";
import { usePortfolioTutorialsState } from "@/tools/aggregate/portfolio-export/PortfolioTutorialContext";
import { PortfolioExportJobSchema } from "@/tools/aggregate/portfolio-export/types";
import { useAdaptationPermissions } from "@/utils/hooks/useAdaptationPermissions";
import { useKeys } from "@/utils/hooks/useKeys";
import { useEffect, useState } from "react";
import { isAnomalyManagementApplied, isDefenceApplied } from "../metadata/utils";
import { AnalysisMetadata, CollectionAsset } from "../types";

export const useAnalysisSettings = ({
  schema,
  shouldFetchAnalysisSettings = true,
  start = 0,
}: {
  schema: PortfolioExportJobSchema;
  shouldFetchAnalysisSettings?: boolean;
  start?: number;
}) => {
  const [keyPair] = useKeys();
  const [analysisSettings, setAnalysisSettings] = useState<AnalysisMetadata | undefined>(undefined);
  const [asset, setAsset] = useState<CollectionAsset | undefined>(undefined);

  const { isTutorialOpen } = usePortfolioTutorialsState();

  const { canRunAutoAdaptation } = useAdaptationPermissions({ isLargeSite: schema.isLargeSite });

  const [isLoading, setIsLoading] = useState(false);

  const pullFirstAsset = async () => {
    if (!keyPair || keyPair.isLoading || !schema) {
      return;
    }
    if (schema.isPrivate ? !keyPair.user : !keyPair.org) {
      return;
    }

    try {
      setIsLoading(true);
      const action = supabaseApi.endpoints.getSuccessfulPortfolioAssets.initiate(
        {
          portfolioId: schema.id,
          start,
          length: 1, // only 1 asset required
        },
        {
          forceRefetch: true,
        }
      );

      const data = await dispatchWithRetries<PortfolioAsset[]>({ action });

      // Decrypt assets.
      const assets = await decryptPortfolioAssets(
        schema.secret,
        data.map(({ data, encrypted_id, id }) => ({ data, encryptedId: encrypted_id, id }))
      );

      const res = await streamResults(
        assets.map(({ uuid }) => uuid),
        schema.stamps
      );

      const results: ResponseSuccess[] = (await res.text())
        .split("\n")
        .filter((line) => !!line.trim())
        .map((line) => JSON.parse(line.replace("\x1E", "")));

      const chunkAssets = assets.map((asset) => {
        const { result } = results.find(({ item_id }) => item_id === asset.uuid)!;
        return {
          ...asset,
          result,
        } as CollectionAsset;
      });

      const firstAsset = chunkAssets.length ? chunkAssets[0] : null;
      if (firstAsset) {
        const { data, result } = firstAsset;

        setAsset(firstAsset);
        setAnalysisSettings({
          autoResilienceApplied: canRunAutoAdaptation && schema.autoAdaptationStatus !== null,
          anomalyManagementApplied: data?.anomaly_management_rp
            ? isAnomalyManagementApplied(data.anomaly_management_rp)
            : null,
          defenceApplied: data?.defences ? isDefenceApplied(data.defences) : null,
          engineVersion: result.metadata.engines_version || null,
          exposuresEnabled: isTutorialOpen
            ? true
            : result.results.rcp85?.exposures !== undefined && result.results.rcp85.exposures !== null,
        });
      }
      setIsLoading(false);
    } catch (err) {
      console.log(err);
      setIsLoading(false);
      setAnalysisSettings(undefined);
      setAsset(undefined);
    }
  };

  useEffect(() => {
    if (shouldFetchAnalysisSettings) {
      pullFirstAsset();
    }
  }, [schema, keyPair, shouldFetchAnalysisSettings]);

  return { analysisSettings, asset, isLoading };
};
